import { SpeedyFlowEvent, SpeedyFlowMachineState, SpeedyFlowName, SpeedyFlowStep } from '../types';
import completionVariantFor from './completion';

/**
 * The step machine. One reducer for all three flows, because the flows differ only in which edges
 * they take — duplicating them per flow is how the sign-in and purchase paths would drift apart.
 *
 * Pure and total: an event a step does not accept returns the state unchanged rather than throwing,
 * so a stray click during an async hop cannot break the modal.
 */

export const initialMachineState = ({
  flow,
  initialStep,
  hasAccount = false,
  hasPersona = false,
  completionVariant,
  productChoice,
}: {
  flow: SpeedyFlowName;
  initialStep: SpeedyFlowStep;
  hasAccount?: boolean;
  hasPersona?: boolean;
  completionVariant?: SpeedyFlowMachineState['completionVariant'];
  productChoice?: SpeedyFlowMachineState['productChoice'];
}): SpeedyFlowMachineState => ({
  flow,
  step: initialStep,
  hasAccount,
  hasPersona,
  purchased: false,
  unconfirmed: false,
  completionVariant,
  productChoice,
});

const isPurchaseFlow = (flow: SpeedyFlowName) => flow === 'guest-purchase' || flow === 'member-purchase';

/**
 * Where a completed authentication goes. In a purchase flow it is the middle of the journey, not
 * the end — the visitor signed up in order to buy something, and still has to.
 *
 * Unless they have not chosen it yet. "I already have an account" leaves the Choose Product Step
 * without a pick, so authentication returns them to the choice. Sending them to the payment step
 * with nothing to buy left them reading "taking you to secure checkout" over a checkout that could
 * never start: no product to name, no session to create, and no way forward.
 */
const afterAuth = (state: SpeedyFlowMachineState): SpeedyFlowMachineState => {
  if (isPurchaseFlow(state.flow)) {
    return { ...state, step: state.productChoice ? 'await-payment' : 'choose-product' };
  }
  return {
    ...state,
    step: 'completion',
    completionVariant: completionVariantFor({ purchased: false, hasPersona: state.hasPersona }),
  };
};

/**
 * Where a visitor who has just proved who they are lands, whichever way they proved it.
 *
 * Shared by signing in on the Log-In Step and by signing in somewhere this flow cannot see, so the
 * two cannot answer the same question differently.
 */
const afterSignIn = (state: SpeedyFlowMachineState, isRealEmail: boolean): SpeedyFlowMachineState => {
  const authed = { ...state, hasAccount: true };
  if (!isRealEmail) {
    return { ...authed, step: 'verify-email' };
  }
  // A returning visitor resuming a Sign-In Flow has nothing to be told — unless they have no
  // persona, in which case this is the only place they will ever be asked, because the
  // "I already have an account" branch skipped the opening persona step.
  if (!isPurchaseFlow(state.flow) && state.hasPersona) {
    return { ...authed, step: 'closed' };
  }
  return afterAuth(authed);
};

/** The steps that exist only because the visitor has not authenticated yet. */
const AUTH_STEPS: SpeedyFlowMachineState['step'][] = ['persona', 'sign-up', 'log-in', 'verify-email'];

/**
 * How the flow ends, however it got there.
 *
 * The interesting part is not where they were but whether we still owe them a question: a visitor
 * who made an account *during this flow* and never gave a persona is asked on the way out. This is
 * the last chance — there is no skip control by design.
 *
 * The Member Purchase Flow is excluded because its visitor arrived already signed in: showing them
 * an account confirmation for an account they have had for months reads as a bug.
 */
const finish = (state: SpeedyFlowMachineState): SpeedyFlowMachineState => {
  const owesPersona = state.flow !== 'member-purchase' && state.hasAccount && !state.hasPersona;
  if (state.step !== 'completion' && owesPersona) {
    return {
      ...state,
      step: 'completion',
      completionVariant: completionVariantFor({ purchased: state.purchased, hasPersona: false }),
    };
  }
  return { ...state, step: 'closed' };
};

const nextStep = (state: SpeedyFlowMachineState, event: SpeedyFlowEvent): SpeedyFlowMachineState => {
  // Dismissal is handled once, for every step — see `finish`.
  if (event.type === 'dismissed') {
    return finish(state);
  }

  // Also handled once for every step, because none of it is about where they are standing: the
  // world moved underneath the flow, and it has to catch up.
  if (event.type === 'factsChanged') {
    const { facts } = event;
    const unchanged =
      state.hasAccount === facts.hasAccount &&
      state.hasPersona === facts.hasPersona &&
      state.gateSatisfied === facts.gateSatisfied;
    const merged: SpeedyFlowMachineState = {
      ...state,
      hasAccount: facts.hasAccount,
      hasPersona: facts.hasPersona,
      gateSatisfied: facts.gateSatisfied,
    };

    // The confirmation card may be asking for a persona the visitor turns out to already have.
    // Every in-tab path gets here on its own event, dispatched before this one could arrive, so it
    // decides using the `hasPersona` read when the flow opened — from a signed-out visitor, so
    // always false. Correct the card rather than leaving it asking a question with an answer.
    if (state.step === 'completion' && facts.hasPersona && !state.hasPersona) {
      return { ...merged, completionVariant: completionVariantFor({ purchased: state.purchased, hasPersona: true }) };
    }

    // Otherwise the payment step and the confirmation own their own endings. A payment may be in
    // flight, and the entitlement landing is precisely what `paymentConfirmed` is for — it says
    // "That worked!" rather than letting the modal vanish out from under someone just charged.
    if (state.step === 'await-payment' || state.step === 'completion') {
      return unchanged ? state : merged;
    }

    // They hold what this flow was for, so it is over — and it ends the way walking away would,
    // persona question included. Only on the *transition*: satisfied from the very first report
    // means the flow was opened for a satisfied gate deliberately, which an explicit `ppt` does.
    //
    // An unverified address is not "over", whatever the gate says. An account gate is satisfied the
    // instant there is a session, and signing up creates one before the address is confirmed — so
    // without this the Sign-In Flow congratulated people on the way past the verification step they
    // had just been sent to.
    if (state.gateSatisfied === false && facts.gateSatisfied && facts.isRealEmail) {
      return finish(merged);
    }

    // An account the machine did not know about. Every path that authenticates inside this modal
    // tells it directly, so one that appears without being announced was made somewhere else —
    // the other tab a magic link opened, most often.
    if (!state.hasAccount && facts.hasAccount && AUTH_STEPS.includes(state.step)) {
      return afterSignIn(merged, facts.isRealEmail);
    }

    // The address was confirmed somewhere else, which is the ordinary way it happens: the link goes
    // to whichever tab the mail client picks, and this one is left waiting for a code that has
    // already been accepted. Lands exactly where verifying here would have — the gate rule above
    // has already taken the flows that are simply over.
    if (state.step === 'verify-email' && facts.isRealEmail) {
      return afterAuth({ ...merged, hasAccount: true });
    }

    return unchanged ? state : merged;
  }

  switch (state.step) {
    case 'persona':
      if (event.type === 'chosePersona') {
        return { ...state, hasPersona: true, persona: event.persona };
      }
      if (event.type === 'choseSignUp') {
        return { ...state, step: 'sign-up' };
      }
      if (event.type === 'choseLogIn') {
        return { ...state, step: 'log-in' };
      }
      return state;

    case 'choose-product':
      if (event.type === 'choseProduct') {
        const { type: _type, ...productChoice } = event;
        return {
          ...state,
          productChoice,
          // Keyed on having an account rather than on the flow: a Guest Purchase visitor who took
          // the log-in link is signed in by the time they come back here, and asking them to sign up
          // for the account they just used would strand them.
          step: state.hasAccount ? 'await-payment' : 'sign-up',
        };
      }
      if (event.type === 'choseLogIn') {
        return { ...state, step: 'log-in' };
      }
      return state;

    case 'sign-up':
      if (event.type === 'ssoSucceeded') {
        return afterAuth({ ...state, hasAccount: true });
      }
      if (event.type === 'signedUpByEmail') {
        return { ...state, step: 'verify-email', hasAccount: true };
      }
      // A failed social round trip leaves them where they were, with the form still filled in.
      // Nothing was created, so `hasAccount` must not move.
      if (event.type === 'ssoFailed') {
        return state;
      }
      if (event.type === 'choseLogIn') {
        return { ...state, step: 'log-in' };
      }
      return state;

    case 'log-in':
      if (event.type === 'signedInByEmail') {
        return afterSignIn(state, event.isRealEmail);
      }
      if (event.type === 'choseSignUp') {
        return { ...state, step: 'sign-up' };
      }
      return state;

    case 'verify-email':
      if (event.type === 'emailVerified') {
        return afterAuth({ ...state, hasAccount: true });
      }
      // "Use a different email" (Figma 8306-14454): they mistyped the address, so send them back
      // to the form. The account created against the wrong address is left on the server — it is
      // unverified and harmless, and cleaning it up is not this modal's business — but the visitor
      // is signed out of it, so as far as the flow is concerned there is no account again.
      if (event.type === 'choseSignUp') {
        return { ...state, step: 'sign-up', hasAccount: false };
      }
      return state;

    case 'await-payment':
      if (event.type === 'paymentConfirmed' || event.type === 'upgradedInPlace') {
        return {
          ...state,
          step: 'completion',
          purchased: true,
          unconfirmed: false,
          completionVariant: completionVariantFor({ purchased: true, hasPersona: state.hasPersona }),
        };
      }
      // The card was charged; the entitlement just has not landed. Deliberately not an error state.
      if (event.type === 'paymentUnconfirmed') {
        return { ...state, unconfirmed: true };
      }
      if (event.type === 'paymentAbandoned') {
        return { ...state, step: 'choose-product', unconfirmed: false };
      }
      // Refused, not abandoned — and the choice goes with it. Keeping it would put the visitor back
      // on the product step with a pick restored that they have just been told they cannot have,
      // and send them round again if they pressed on.
      if (event.type === 'checkoutRefused') {
        return { ...state, step: 'choose-product', unconfirmed: false, productChoice: undefined };
      }
      return state;

    case 'completion':
      if (event.type === 'chosePersona') {
        return { ...state, hasPersona: true, persona: event.persona };
      }
      return state;

    default:
      return state;
  }
};

export default nextStep;
